import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { useForm, ValidationError } from '@formspree/react';

const ContactSection: React.FC = () => {
  const [state, handleSubmit] = useForm('xjkwnovr');

  if (state.succeeded) {
    return (
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="bg-green-50 rounded-lg p-8 text-center">
            <p className="text-green-800 text-lg">
              Bedankt voor uw bericht! We nemen zo spoedig mogelijk contact met u op.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white p-8 rounded-lg shadow-lg"
          >
            <h2 className="text-3xl font-bold mb-6 text-navy-800">Neem Contact Op</h2>
            <p className="text-gray-600 mb-8">
              Heeft u vragen of wilt u een afspraak maken? Vul het formulier in en wij
              nemen zo snel mogelijk contact met u op.
            </p>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Naam
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                    placeholder="Uw naam"
                  />
                  <ValidationError prefix="Name" field="name" errors={state.errors} />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    E-mail
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                    placeholder="Uw e-mailadres"
                  />
                  <ValidationError prefix="Email" field="email" errors={state.errors} />
                </div>
              </div>
              <div className="mb-6">
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Telefoonnummer
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                  placeholder="Uw telefoonnummer"
                />
                <ValidationError prefix="Phone" field="phone" errors={state.errors} />
              </div>
              <div className="mb-6">
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Bericht
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                  placeholder="Uw bericht"
                ></textarea>
                <ValidationError prefix="Message" field="message" errors={state.errors} />
              </div>
              <button
                type="submit"
                disabled={state.submitting}
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 w-full disabled:opacity-50 shadow-lg shadow-orange-500/20 hover:shadow-xl hover:shadow-orange-500/30 hover:-translate-y-0.5 disabled:hover:translate-y-0"
              >
                {state.submitting ? 'Bezig met verzenden...' : 'Verstuur bericht'}
              </button>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-navy-800 p-8 rounded-lg shadow-lg text-white mb-8">
              <h3 className="text-2xl font-bold mb-6">Contactgegevens</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <MapPin size={20} className="mr-3 mt-1 text-orange-500" />
                  <div>
                    <p className="font-medium">Adres</p>
                    <p>Installatieweg 5-1, 8263 BS Kampen</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Phone size={20} className="mr-3 mt-1 text-orange-500" />
                  <div>
                    <p className="font-medium">Telefoon</p>
                    <a
                      href="tel:0383336549"
                      className="hover:text-orange-300 transition-colors"
                    >
                      038-3336549
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <Mail size={20} className="mr-3 mt-1 text-orange-500" />
                  <div>
                    <p className="font-medium">E-mail</p>
                    <a
                      href="mailto:info@htselectrotechniek.nl"
                      className="hover:text-orange-300 transition-colors"
                    >
                      info@htselectrotechniek.nl
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <Clock size={20} className="mr-3 mt-1 text-orange-500" />
                  <div>
                    <p className="font-medium">Bereikbaarheid</p>
                    <p>24 uur per dag voor storingen en noodgevallen</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden shadow-lg h-64 bg-gray-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2413.7505!2d5.8774364!3d52.5682513!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c879d0a802fb2f%3A0x25f2d6fa069ee07!2sHTS%20Electrotechniek!5e0!3m2!1snl!2snl!4v1710427714841!5m2!1snl!2snl"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                title="HTS Electrotechniek locatie"
              ></iframe>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;